import { Anchor, Anim, Between, Handles, keys, outline, Walk } from '../stage';

/** Select: point at a shape, it is chosen, drag it along. */
export function SelectDemo() {
  return <>
    <g>
      <animateTransform attributeName="transform" type="translate" {...keys(['0 0', '0 0', '62 0', '62 0'], [0, 0.42, 0.72, 1])} />
      <rect className="g-obj" x={62} y={34} width={52} height={42} rx={6} />
      <g><Between from={0.3} to={0.97} /><rect className="g-sel" x={62} y={34} width={52} height={42} /><Handles x={62} y={34} w={52} h={42} /></g>
    </g>
    <Walk k="select" stops={[[190, 96, 0.05], [90, 56, 0.3], [90, 56, 0.42], [152, 56, 0.72]]} />
  </>;
}

/** Rectangle: drag from corner to corner. */
export function RectDemo() {
  return <>
    <rect className="g-new" x={62} y={26}>
      <Anim attr="width" values={[0, 0, 116, 116]} times={[0, 0.15, 0.6, 1]} />
      <Anim attr="height" values={[0, 0, 60, 60]} times={[0, 0.15, 0.6, 1]} />
    </rect>
    <g><Between from={0.62} to={0.97} /><Handles x={62} y={26} w={116} h={60} /></g>
    <Walk k="rect" stops={[[62, 26, 0.12], [178, 86, 0.6]]} />
  </>;
}

/** Pen: click, drag a curve, click — the path grows point by point. */
export function PenDemo() {
  const d = 'M46 82C76 82 88 30 120 30S170 80 194 80';
  return <>
    <path className="g-path-new" d={d} pathLength={1} strokeDasharray="1 3">
      <Anim attr="stroke-dashoffset" values={[1.2, 1.2, 0.5, 0.5, 0, 0]} times={[0, 0.34, 0.46, 0.66, 0.74, 1]} />
    </path>
    <g><Between from={0.4} to={0.97} /><path className="g-handle-line" d="M96 30H144" /><circle className="g-handle-dot" cx={96} cy={30} r={2} /><circle className="g-handle-dot" cx={144} cy={30} r={2} /></g>
    <g><Between from={0.14} to={0.97} /><Anchor x={46} y={82} /></g>
    <g><Between from={0.34} to={0.97} /><Anchor x={120} y={30} /></g>
    <g><Between from={0.66} to={0.97} /><Anchor x={194} y={80} chosen /></g>
    <Walk k="pen" stops={[[46, 82, 0.12], [46, 82, 0.16], [120, 30, 0.34], [144, 30, 0.46], [194, 80, 0.66]]} />
  </>;
}

/** Scissors: press on an edge and the ring opens there. */
export function ScissorsDemo() {
  // The ring starts at the cut, so opening it is only a gap in its dash.
  const ring = 'M152 56A32 32 0 1 1 152 55.99';
  return <>
    <path className="g-path" d={ring} pathLength={100}>
      <Anim attr="stroke-dasharray" values={['100 0', '100 0', '93 7', '93 7']} times={[0, 0.38, 0.46, 1]} />
      <Anim attr="stroke-dashoffset" values={[0, 0, -3.5, -3.5]} times={[0, 0.38, 0.46, 1]} />
    </path>
    <g><Between from={0.46} to={0.97} /><Anchor x={150} y={50} chosen /><Anchor x={150} y={62} chosen /></g>
    <Walk k="cut:scissors" stops={[[212, 90, 0.06], [153, 56, 0.34], [153, 56, 0.5], [206, 30, 0.72]]} />
  </>;
}

/** Twirl: hold the brush on an outline and it winds round the brush. */
export function TwirlDemo() {
  const cx = 120;
  const cy = 56;
  const square: [number, number][] = [];
  const N = 64;
  for (let i = 0; i < N; i++) {
    // A rounded square, sampled evenly round its middle.
    const a = (i / N) * Math.PI * 2;
    const r = 30 / Math.max(Math.abs(Math.cos(a)), Math.abs(Math.sin(a))) ** 0.85;
    square.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]);
  }
  const twist = square.map(([x, y]) => {
    const dx = x - cx;
    const dy = y - cy;
    const r = Math.hypot(dx, dy);
    const turn = 1.35 * (1 - (r - 30) / 12);
    return [cx + dx * Math.cos(turn) - dy * Math.sin(turn), cy + dx * Math.sin(turn) + dy * Math.cos(turn)] as [number, number];
  });
  return <>
    <path className="g-new" d={outline(square)}>
      <Anim attr="d" values={[outline(square), outline(square), outline(twist), outline(twist)]} times={[0, 0.25, 0.7, 1]} />
    </path>
    <circle className="g-brush" cx={cx} cy={cy} r={40}>
      <Between from={0.2} to={0.74} />
    </circle>
    <Walk k="liquify:twirl" stops={[[200, 96, 0.04], [cx, cy, 0.22], [cx, cy, 0.74], [196, 24, 0.9]]} />
  </>;
}

/** Blend: click one shape, then the other, and the steps between appear. */
export function BlendDemo() {
  const steps = [1, 2, 3, 4, 5];
  return <>
    <circle className="g-blend-a" cx={42} cy={56} r={14} />
    <rect className="g-blend-b" x={184} y={42} width={28} height={28} rx={4} />
    {steps.map((i) => {
      const f = i / 6;
      const x = 42 + (198 - 42) * f;
      const size = 28;
      const radius = 14 * (1 - f) + 4 * f;
      const at = 0.44 + i * 0.035;
      return (
        <rect key={i} className="g-blend-step" x={x - size / 2} y={56 - size / 2} width={size} height={size} rx={radius} style={{ ['--f' as string]: f }}>
          <Between from={at} to={0.97} />
        </rect>
      );
    })}
    <Walk k="blend" stops={[[120, 100, 0.04], [42, 56, 0.18], [42, 56, 0.24], [198, 56, 0.4], [198, 56, 0.97]]} />
  </>;
}

